import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { ShieldQuestion, Check, X, Loader2, Eye, EyeOff } from "lucide-react";

const NAV = [
  {to:"/founder/dashboard",label:"Dashboard"},
  {to:"/founder/tutorial",label:"Tutorial"},
  {to:"/founder/team",label:"Team"},
  {to:"/onboarding",label:"Onboarding"},
];

export default function FounderInterests() {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    const { data } = await api.get("/companies/me/interests");
    setRows(data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision, note = "") => {
    setBusyId(id);
    try { await api.post(`/companies/me/interests/${id}/decide`, { decision, note }); await load(); }
    finally { setBusyId(null); }
  };

  const filtered = rows.filter(r => filter === "all" || r.status === filter);
  const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <AppShell nav={NAV}>
      <div className="flex items-center gap-2">
        <ShieldQuestion className="h-5 w-5 text-[#0A8B85]"/>
        <h1 className="text-3xl font-bold text-neutral-900">Funder interest</h1>
      </div>
      <p className="mt-1 text-sm text-neutral-600">
        Funders who have requested access to your business. You decide who sees your information. Their identities are anonymised until you approve.
      </p>

      <div className="mt-6 flex gap-2 border-b border-neutral-200">
        {["pending","approved","declined","all"].map(k => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`interest-filter-${k}`}
            className={"px-3 py-2 text-sm font-semibold " +
              (filter === k ? "border-b-2 border-[#0A8B85] text-[#0A8B85]" : "text-neutral-500 hover:text-neutral-700")}>
            {k.charAt(0).toUpperCase() + k.slice(1)} {counts[k] ? <span className="ml-1 rounded-full bg-neutral-200 px-1.5 text-[10px] text-neutral-700">{counts[k]}</span> : null}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3" data-testid="interests-list">
        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500">
            No {filter} interests yet. As funders discover your business and request access, they'll appear here.
          </div>
        )}
        {filtered.map(r => {
          const p = r.investor_profile || {};
          return (
            <div key={r.interest_id} className="rounded-2xl border border-neutral-200 bg-white p-5" data-testid={`interest-${r.interest_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-[#0A8B85] px-3 py-0.5 text-[11px] font-bold text-white">
                      <EyeOff className="inline h-3 w-3 mr-1"/>Anonymous funder · {p.type}
                    </span>
                    <span className="text-xs text-neutral-500">· {new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="mt-2 text-sm text-neutral-700">{p.description}</p>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                    <Cell label="Type" value={p.type}/>
                    <Cell label="AUM" value={p.aum_band}/>
                    <Cell label="Ticket" value={(p.ticket_min || p.ticket_max) ? `$${((p.ticket_min||0)/1000).toFixed(0)}K–$${((p.ticket_max||0)/1000).toFixed(0)}K` : "—"}/>
                    <Cell label="Stages" value={(p.stages || []).join(", ") || "Any"}/>
                    <Cell label="Sectors" value={(p.sectors || []).join(", ") || "Any"}/>
                    <Cell label="Countries" value={(p.countries || []).join(", ") || "Any"}/>
                  </div>
                </div>
                {r.status === "pending" ? (
                  <div className="flex flex-col gap-2">
                    <button onClick={() => decide(r.interest_id, "approve")} disabled={busyId === r.interest_id}
                      data-testid={`interest-approve-${r.interest_id}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-[#0A8B85] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#066667] disabled:opacity-50">
                      {busyId === r.interest_id ? <Loader2 className="h-3 w-3 animate-spin"/> : <Check className="h-3 w-3"/>}
                      Approve
                    </button>
                    <button onClick={() => decide(r.interest_id, "decline")} disabled={busyId === r.interest_id}
                      data-testid={`interest-decline-${r.interest_id}`}
                      className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">
                      <X className="h-3 w-3"/> Decline
                    </button>
                  </div>
                ) : (
                  <span className={"rounded-full px-3 py-1 text-[11px] font-bold uppercase " +
                    (r.status === "approved" ? "bg-green-100 text-green-700" : "bg-neutral-100 text-neutral-600")}>
                    {r.status}
                  </span>
                )}
              </div>
              <p className="mt-3 text-[11px] text-neutral-500">
                <Eye className="inline h-3 w-3 mr-0.5"/> Approving grants this funder full VDR access. Declining notifies them you're not ready to engage.
              </p>
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}

const Cell = ({label, value}) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
    <p className="font-semibold text-neutral-800">{value || "—"}</p>
  </div>
);
